import type { Version } from '@keboola/api-client/developerPortal/types';

import { pinOf, pinsAfterRollback, readPins, toWaveTags } from '../admin/waves';

/** One attribute that differs between two versions of a component. */
export type AttributeChange = { attribute: string; oldValue: unknown; newValue: unknown };

/**
 * Fields every version carries with its own value, so they are never a change.
 *
 * - The same fields apps-api leaves out of `GET /admin/changes`.
 */
const VERSION_METADATA = new Set([
  'version',
  'createdBy',
  'createdOn',
  'updatedBy',
  'updatedOn',
  'vendor',
]);

/** JSON with object keys sorted, so two values that differ only in key order compare equal. */
const canonical = (value: unknown): string =>
  JSON.stringify(value ?? null, (_key, inner: unknown) =>
    inner !== null && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.fromEntries(Object.entries(inner).sort(([a], [b]) => a.localeCompare(b)))
      : inner,
  );

/**
 * The attributes that differ from `from` to `to`, sorted by name.
 *
 * - A missing attribute counts as `null`, so adding or clearing a field is a change.
 */
export const diffVersions = (from: Version, to: Version): AttributeChange[] =>
  [...new Set([...Object.keys(from), ...Object.keys(to)])]
    .filter((attribute) => !VERSION_METADATA.has(attribute))
    .filter((attribute) => canonical(from[attribute]) !== canonical(to[attribute]))
    .sort((a, b) => a.localeCompare(b))
    .map((attribute) => ({
      attribute,
      oldValue: from[attribute] ?? null,
      newValue: to[attribute] ?? null,
    }));

/**
 * Derived fields a rollback does not restore.
 *
 * - apps-api copies the chosen version through `formatAppInput`'s allow-list; these are output-only.
 * - The current version's publishing request stays, so its derived state would preview wrong.
 */
const NOT_RESTORED_BY_ROLLBACK = new Set(['publishingStatus', 'publishingRequest', 'uri']);

/**
 * What reverting from `current` to `target` changes, as apps-api's rollback applies it.
 *
 * - `waveTags` are not copied from `target`: pins newer than it move to it, older pins stay.
 */
export const revertPreview = (current: Version, target: Version): AttributeChange[] => {
  const changes = diffVersions(current, target).filter(
    (change) => !NOT_RESTORED_BY_ROLLBACK.has(change.attribute) && change.attribute !== 'waveTags',
  );
  const targetPin = pinOf(target);
  const pins = readPins(current.waveTags);
  if (targetPin === null || pins.size === 0) return changes;

  const before = toWaveTags(pins);
  const after = toWaveTags(pinsAfterRollback(pins, targetPin));
  if (canonical(before) === canonical(after)) return changes;
  return [...changes, { attribute: 'waveTags', oldValue: before, newValue: after }].sort((a, b) =>
    a.attribute.localeCompare(b.attribute),
  );
};

/** Versions newest first; a row without a `version` number sorts last. */
export const newestFirst = (versions: Version[]): Version[] =>
  [...versions].sort((a, b) => (b.version ?? -1) - (a.version ?? -1));
